import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

const AREAS_QUERY_KEY = 'areas' as const;
const SHIPPING_CATEGORY_KEY = 'shipping-category' as const;
const ORDER_QUERY_KEY = 'order-detail' as const;
const ORDER_LOCATION_PICKUP_KEY = 'order-location-pickup' as const;
const ORDER_SETTINGS_KEY = 'order-settings' as const;

export const areasQueryKeys = queryKeysFactory(AREAS_QUERY_KEY);
export const shippingCategoryQueryKeys = queryKeysFactory(SHIPPING_CATEGORY_KEY);
export const ordersQueryKeys = queryKeysFactory(ORDER_QUERY_KEY);
export const orderLocationPickupQueryKeys = queryKeysFactory(ORDER_LOCATION_PICKUP_KEY);
export const orderSettingsQueryKeys = queryKeysFactory(ORDER_SETTINGS_KEY);

export const useCreateExternalOrder = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.StoreOrderCreateResponse>,
    FetchError,
    HttpTypes.StoreCreateOrder & { host: string }
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.site.orders.create(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useAreas = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<HttpTypes.StoreAreaResponse, FetchError, HttpTypes.StoreAreaResponse, QueryKey>,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: areasQueryKeys.list(query),
    queryFn: async () => sdk.site.orders.listArea(query),
    ...options,
  });

  return { ...data, ...rest };
};

export const useShippingCategory = (
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StoreShippingCategoryResponse,
      FetchError,
      HttpTypes.StoreShippingCategoryResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: shippingCategoryQueryKeys.list(),
    queryFn: async () => sdk.site.orders.listShippingCategory(),
    ...options,
  });

  return { data, ...rest };
};

export const useGetShippingRates = (
  locationId?: string,
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.StoreCreateShippingRatesResponse>,
    FetchError,
    HttpTypes.StoreCreateShippingRates
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.site.orders.createRates(payload, locationId as string),

    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useExternalOrderDetail = (
  id: string,
  token: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StoreCustomerOrderExternalDetailResponse,
      FetchError,
      HttpTypes.StoreCustomerOrderExternalDetailResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: ordersQueryKeys.detail(id as string),
    queryFn: async () => sdk.site.orders.detail(id as string, token as string),
    enabled: !!id && options?.enabled,
    ...options,
  });

  return { ...data, ...rest };
};

export const useGetSuccessOrder = (
  id: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<{ no_order: string }>,
      FetchError,
      HttpTypes.IResponse<{ no_order: string }>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: ['success-order-payment', id],
    queryFn: async () => {
      const response = await sdk.site.orders.successPayment(id as string);
      return {
        ...response,
        data: {
          no_order: response.data.order_no,
        },
      };
    },
    ...options,
  });

  return { ...data, ...rest };
};

export const useGetLocationPickup = (
  payload: {
    items: string[];
  },
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StoreOrderLocationPickupResponse,
      FetchError,
      HttpTypes.StoreOrderLocationPickupResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: orderLocationPickupQueryKeys.list(payload.items),
    queryFn: async () => {
      const res = await sdk.site.orders.listLocationPickup(payload.items);
      return res;
    },
    enabled: options?.enabled,
    ...options,
  });

  return { ...data, ...rest };
};

export const useGetOrderSettings = (
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StoreOrderSettingsResponse,
      FetchError,
      HttpTypes.StoreOrderSettingsResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: orderSettingsQueryKeys.details(),
    queryFn: async () => {
      const res = await sdk.site.orders.orderSettings();
      return res;
    },
    enabled: options?.enabled,
    ...options,
  });

  return { ...data, ...rest };
};
